import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Shield, Zap, ArrowLeft, CheckCircle2, AlertTriangle, AlertOctagon,
  Wrench, RefreshCw, Loader2,
} from "lucide-react";
import LanguageSwitcher from "../components/LanguageSwitcher";
import UptimeSparkline from "../components/UptimeSparkline";
import PublicFooter from "../components/PublicFooter";
import usePageMeta from "../hooks/usePageMeta";

const SEVERITY_PILL = {
  maintenance: "badge-cooling",
  minor: "badge-pending",
  major: "badge-suspended",
  critical: "badge-rejected",
};

function ComponentRow({ tKey, status, t, history, summary }) {
  const ok = status === "operational";
  return (
    <div className="flex items-center justify-between gap-4 py-3.5 border-t border-[#114232]/10 flex-wrap" data-testid={`status-component-${tKey}`}>
      <div className="flex items-center gap-2.5 min-w-[140px]">
        {ok ? (
          <CheckCircle2 className="w-4 h-4 text-[#114232]" strokeWidth={1.8} />
        ) : (
          <AlertTriangle className="w-4 h-4 text-[#c15337]" strokeWidth={1.8} />
        )}
        <span className="text-sm font-medium text-[#1b1d1c]">{t(`status.${tKey}`)}</span>
      </div>
      <div className="flex-1 min-w-[120px] flex justify-center">
        <UptimeSparkline points={history || []} testid={`status-sparkline-${tKey}`} />
      </div>
      <div className="flex flex-col items-end min-w-[120px]">
        <span className={`text-xs font-semibold uppercase tracking-wider ${ok ? "text-[#114232]" : "text-[#c15337]"}`}>
          {status}
        </span>
        {summary?.uptime_pct != null && (
          <span
            className="text-[10px] text-[#5c6360] font-mono mt-0.5"
            data-testid={`status-uptime-${tKey}`}
          >
            {t("status_history.uptime_pct", { pct: summary.uptime_pct })}
          </span>
        )}
      </div>
    </div>
  );
}

export default function StatusPage() {
  const { t } = useTranslation();
  usePageMeta(
    "Status · Nyoksure",
    "Live operational status of Nyoksure: payment verification, WhatsApp FlashClaim, merchant portal, AI risk scoring and support response.",
  );
  const [data, setData] = useState(null);
  const [history, setHistory] = useState({ series: {}, summary: {} });
  const [busy, setBusy] = useState(false);

  async function load() {
    setBusy(true);
    try {
      const base = process.env.REACT_APP_BACKEND_URL;
      const [statusRes, histRes] = await Promise.all([
        fetch(`${base}/api/status`),
        fetch(`${base}/api/status/history?days=7`),
      ]);
      setData(await statusRes.json());
      if (histRes.ok) setHistory(await histRes.json());
    } finally {
      setBusy(false);
    }
  }

  useEffect(() => {
    load();
    const id = setInterval(load, 60_000);
    // SSE: when the admin SSE feed reports a fresh heartbeat with non-zero
    // open_incidents change, trigger an immediate refresh of status + history
    // instead of waiting on the 60s timer.
    let es = null;
    try {
      es = new EventSource(`${process.env.REACT_APP_BACKEND_URL}/api/admin/stream`);
      let lastOpen = null;
      es.addEventListener("heartbeat", (ev) => {
        try {
          const h = JSON.parse(ev.data);
          if (lastOpen !== null && h.open_incidents !== lastOpen) {
            load();
          }
          lastOpen = h.open_incidents;
        } catch (_) { /* ignore */ }
      });
    } catch (_) { /* ignore — public viewers cannot subscribe */ }
    return () => {
      clearInterval(id);
      es && es.close();
    };
  }, []);

  if (!data) {
    return (
      <div className="min-h-screen bg-[#f4f2eb] flex items-center justify-center" data-testid="status-loading">
        <Loader2 className="w-6 h-6 animate-spin text-[#114232]" />
      </div>
    );
  }

  const OVERALL = {
    operational: { color: "text-[#114232]", bg: "bg-[#114232]/8 ring-1 ring-[#114232]/20",
                   icon: CheckCircle2, label: t("status.operational") },
    partial_outage: { color: "text-[#8a5a16]", bg: "bg-[#d49a36]/12 ring-1 ring-[#d49a36]/30",
                      icon: AlertTriangle, label: t("status.partial_outage") },
    major_outage: { color: "text-[#8b3a23]", bg: "bg-[#c15337]/12 ring-1 ring-[#c15337]/30",
                    icon: AlertOctagon, label: t("status.major_outage") },
    degraded: { color: "text-[#8a5a16]", bg: "bg-[#d49a36]/12 ring-1 ring-[#d49a36]/30",
                icon: AlertTriangle, label: t("status.degraded") },
  };
  const o = OVERALL[data.overall_status] || OVERALL.operational;
  const OverallIcon = o.icon;
  const COMPONENT_KEYS = ["api", "database", "uploads"];

  return (
    <div className="min-h-screen bg-[#f4f2eb] text-[#1b1d1c]" data-testid="status-page">
      <nav className="px-6 lg:px-10 py-5 flex items-center justify-between max-w-4xl mx-auto">
        <Link to="/" className="flex items-center gap-2.5">
          <div className="relative">
            <Shield className="w-7 h-7 text-[#114232]" strokeWidth={1.5} />
            <Zap className="w-3 h-3 text-[#c15337] absolute -bottom-0.5 -right-0.5" fill="#c15337" strokeWidth={0} />
          </div>
          <span className="font-heading text-xl font-bold tracking-tight text-[#114232]">Nyoksure</span>
        </Link>
        <div className="flex items-center gap-3 text-sm">
          <LanguageSwitcher compact />
          <button
            onClick={load} disabled={busy}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold uppercase tracking-wider text-[#5c6360] hover:text-[#114232] transition-colors"
            data-testid="status-refresh"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${busy ? "animate-spin" : ""}`} /> {t("status.refresh")}
          </button>
          <Link to="/" className="inline-flex items-center gap-1 text-[#5c6360] hover:text-[#114232]">
            <ArrowLeft className="w-3.5 h-3.5" /> {t("status.back")}
          </Link>
        </div>
      </nav>

      <main className="px-6 lg:px-10 max-w-4xl mx-auto pb-20">
        <div className={`mt-4 rounded-2xl p-7 ${o.bg}`} data-testid="status-overall">
          <div className="flex items-center gap-4">
            <OverallIcon className={`w-9 h-9 ${o.color}`} strokeWidth={1.8} />
            <div>
              <div className="text-[11px] font-bold uppercase tracking-[0.22em] text-[#c15337]">{t("status.service_status")}</div>
              <div className={`font-heading text-3xl font-bold mt-1 ${o.color}`} data-testid="status-overall-label">
                {o.label}
              </div>
              <div className="text-xs text-[#5c6360] mt-1 font-mono">
                {t("status.updated")} {new Date(data.generated_at).toLocaleString()} · v{data.components.version}
              </div>
            </div>
          </div>
        </div>

        <section className="mt-8 bg-white rounded-2xl border border-[#114232]/10 p-6" data-testid="status-components-card">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div>
              <div className="font-heading text-lg font-bold text-[#114232] mb-1">{t("status.components")}</div>
              <div className="text-xs text-[#5c6360] mb-2">{t("status.components_subtitle")}</div>
            </div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-[#5c6360] font-semibold" data-testid="status-history-window">
              {t("status_history.title", { days: history?.days || 7 })}
            </div>
          </div>
          {COMPONENT_KEYS.map((k) => (
            <ComponentRow
              key={k}
              tKey={k}
              status={data.components[k]}
              t={t}
              history={history?.series?.[k]}
              summary={history?.summary?.[k]}
            />
          ))}
        </section>

        <section className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="status-integrations">
          {["payments_mode", "whatsapp_mode", "email_mode", "storage_mode"].map((k) => {
            const v = data.components[k];
            const isLive = v === "live" || v === "s3";
            return (
              <div key={k} className="metric-card text-center">
                <div className="text-[10px] uppercase tracking-[0.18em] text-[#5c6360] font-semibold">
                  {k.replace(/_/g, " ")}
                </div>
                <div className={`mt-2 text-base font-bold uppercase tracking-wider ${isLive ? "text-[#114232]" : "text-[#5c6360]"}`}>
                  {v}
                </div>
              </div>
            );
          })}
        </section>

        {data.scheduled_maintenance?.length > 0 && (
          <section className="mt-8" data-testid="status-maintenance">
            <h2 className="font-heading text-lg font-bold text-[#114232] flex items-center gap-2 mb-3">
              <Wrench className="w-4 h-4" /> {t("status.scheduled_maintenance")}
            </h2>
            <div className="space-y-3">
              {data.scheduled_maintenance.map((i) => (
                <div key={i.id} className="bg-white rounded-xl border border-[#d49a36]/30 p-5">
                  <div className="flex items-center justify-between">
                    <div className="font-semibold text-sm text-[#114232]">{i.title}</div>
                    <span className="badge-status badge-cooling">{new Date(i.scheduled_for).toLocaleString()}</span>
                  </div>
                  <div className="text-xs text-[#5c6360] mt-1">{t("status.component")}: {i.component}</div>
                </div>
              ))}
            </div>
          </section>
        )}

        <section className="mt-8" data-testid="status-incidents">
          <h2 className="font-heading text-lg font-bold text-[#114232] mb-3">{t("status.open_incidents")}</h2>
          {data.open_incidents.length === 0 ? (
            <div className="bg-white rounded-xl border border-[#114232]/10 p-6 text-sm text-[#5c6360] text-center">
              {t("status.no_incidents")}
            </div>
          ) : (
            <div className="space-y-3">
              {data.open_incidents.map((i) => (
                <div key={i.id} className="bg-white rounded-xl border border-[#114232]/10 p-5" data-testid={`incident-${i.id}`}>
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div>
                      <div className="font-semibold text-sm text-[#114232]">{i.title}</div>
                      <div className="text-xs text-[#5c6360] mt-0.5">
                        {i.component} · {t("status.started")} {new Date(i.created_at).toLocaleString()}
                      </div>
                    </div>
                    <div className="flex gap-1.5">
                      <span className={`badge-status ${SEVERITY_PILL[i.severity] || "badge-pending"}`}>{i.severity}</span>
                      <span className="badge-status badge-review">{i.status}</span>
                    </div>
                  </div>
                  {i.updates?.length > 0 && (
                    <ul className="mt-3 space-y-2 border-t border-[#114232]/10 pt-3">
                      {i.updates.slice(-3).reverse().map((u) => (
                        <li key={u.id} className="text-xs">
                          <span className="font-semibold uppercase tracking-wider text-[#c15337] mr-2">{u.status}</span>
                          <span className="text-[#1b1d1c]">{u.message}</span>
                          <span className="text-[#5c6360] ml-2 font-mono">
                            · {new Date(u.timestamp).toLocaleString()}
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}
            </div>
          )}
        </section>

        {data.recent_resolved?.length > 0 && (
          <section className="mt-8" data-testid="status-resolved">
            <h2 className="font-heading text-lg font-bold text-[#114232] mb-3">{t("status.recent_history")}</h2>
            <div className="space-y-2">
              {data.recent_resolved.map((i) => (
                <div key={i.id} className="bg-white rounded-xl border border-[#114232]/10 p-4 flex items-center justify-between text-sm">
                  <span className="text-[#1b1d1c]">{i.title}</span>
                  <span className="text-xs text-[#5c6360] font-mono">
                    {t("status.resolved")} {new Date(i.resolved_at).toLocaleDateString()}
                  </span>
                </div>
              ))}
            </div>
          </section>
        )}

        <section className="mt-8 bg-[#d49a36]/8 border border-[#d49a36]/30 rounded-xl p-4 text-sm text-[#8a5a16]"
                  data-testid="status-availability-note">
          <strong className="font-semibold">Service availability may vary</strong> during
          maintenance or third-party outages such as M-Pesa, WhatsApp, hosting, or
          insurer systems.
        </section>
      </main>
      <PublicFooter />
    </div>
  );
}
